import { GoogleAuthProvider, OAuthProvider, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import api from "../api";
import { ReactComponent as AppleLogo } from '../assets/images/apple.svg';
import { ReactComponent as GoogleLogo } from '../assets/images/google.svg';
import { ReactComponent as EmailIcon } from '../assets/images/icons/ic-email.svg';
import { ReactComponent as ArrowIcon } from '../assets/images/icons/ic-arrow.svg';
import LogoBlack from "../assets/images/logo.png";
import MainContext from "../common/MainContext";
import { ButtonStatus } from "../common/constants";
import Button from "../components/Button";
import FooterLayout from "../components/layouts/FooterLayout";
import { auth } from "../firebase";
import style from "./LoginAccess.module.css";

const Login = () => {
    const navigate = useNavigate();
    const { t } = useTranslation()
    const context = useContext(MainContext)

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null)
    const [resetPassword, setResetPassword] = useState(false)
    const [emailSent, setEmailSent] = useState(false)
    const [timer, setTimer] = useState(60)
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)


    const googleProvider = new GoogleAuthProvider()
    googleProvider.setCustomParameters({
        // Localize the Apple authentication screen in Italian.
        locale: 'it'
    });

    const appleProvider = new OAuthProvider('apple.com')
    appleProvider.addScope('email');
    appleProvider.addScope('name');
    appleProvider.setCustomParameters({
        // Localize the Apple authentication screen in Italian.
        locale: 'it'
    });

    useEffect(() => {
        if (auth.currentUser) {
            navigate('/')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.currentUser])

    useEffect(() => {
        if (!emailSent) return;
        const interval = setInterval(() => setTimer(value => Math.max(0, value - 1)), 1000);
        return () => clearInterval(interval);
    }, [emailSent]);

    useEffect(() => {
        if (emailSent && timer === 0) setEmailSent(false);
    }, [emailSent, timer]);

    const googleSignIn = async () => {
        context.isLogging = true
        setLoading(true)
        try {
            await signInWithPopup(auth, googleProvider)
            let user = await api.get("/admin/")
            if (user) {
                context.setUser(user)
                navigate("/")
            }
        }
        catch (error) {
            await auth.signOut()
            setError({ code: -1, message: error.code === 3 ? "Nessun amministratore con queste credenziali" : error.detail })
            console.error("Error Google Sign In: ", error)
        }
        setLoading(false)
        context.isLogging = false
    }

    const appleSignIn = async () => {
        context.isLogging = true
        setLoading(true)
        try {
            await signInWithPopup(auth, appleProvider)
            let user = await api.get("/admin/")
            if (user) {
                context.setUser(user)
                navigate("/")
            }
        }
        catch (error) {
            await auth.signOut()
            setError({ code: -1, message: error.code === 3 ? "Nessun amministratore con queste credenziali" : error.detail })
            console.error("Error Apple Sign In: ", error)
        }
        setLoading(false)
        context.isLogging = false
    }

    const signIn = async () => {
        if (loading || !email.trim() || !password || !validateEmail(email.trim())) return;
        setError(null);
        context.isLogging = true
        setLoading(true)
        try {
            await signInWithEmailAndPassword(auth, email.trim(), password)
            let user = await api.get("/admin/")
            if (user) {
                context.setUser(user)
                if (user.force_change_password) {
                    navigate("/changePassword")
                } else {
                    navigate("/")
                }
            }
        }
        catch (error) {
            await auth.signOut()
            setError({ code: -1, message: error.code === 3 ? "Nessun amministratore con queste credenziali" : error.detail })

            if (error.message?.includes('invalid-credential')) {
                setError({ code: -1, message: t('errors.invalidCredentials') })
            }
            else {
                setError({ code: -1, message: error.message })
            }
        }
        setLoading(false)
        context.isLogging = false
    }

    const validateEmail = (email) => {
        if (email && email.length > 0) {
            return email.match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            )
        }
        else {
            return true
        }
    }

    const handleChangeResetPassword = () => {
        setResetPassword(!resetPassword)
        setError(null)
    }

    const handleResetPassword = async () => {
        if (sending || emailSent || !email.trim() || !validateEmail(email.trim())) return;
        setSending(true);
        setError(null);
        try {
            await sendPasswordResetEmail(auth, email.trim())
            setTimer(60)
            setEmailSent(true)
        }
        catch (error) {
            setError({ code: -1, message: error.message })
        } finally {
            setSending(false)
        }
    }

    const [selectedMethod, setSelectedMethod] = useState(null)

    return (
        <FooterLayout>
            <main className={style.page}>
                <section className={style.card} aria-labelledby="access-title">
                    <img src={LogoBlack} alt="Velv" className={style.logo} />
                    <header className={style.heading}>
                        <span className={style.eyebrow}>Backoffice</span>
                        <h1 id="access-title">{resetPassword ? 'Recupera la password' : selectedMethod === 'email' ? 'Accedi con email' : 'Accedi al tuo account'}</h1>
                        <p>{resetPassword ? 'Inserisci la tua email: riceverai un link per impostare una nuova password.' : 'Usa il tuo account amministratore per accedere alla piattaforma.'}</p>
                    </header>
                    {!resetPassword && !selectedMethod ? <div className={style.methods}>
                        <Button appearance="subtle" disabled={loading} onClick={googleSignIn}><GoogleLogo className={style.icon} />{t('login.signInWithGoogle')}</Button>
                        <Button appearance="subtle" disabled={loading} onClick={appleSignIn}><AppleLogo className={`${style.icon} ${style.appleIcon}`} />{t('login.signInWithApple')}</Button>
                        <div className={style.divider}><span>oppure</span></div>
                        <Button disabled={loading} onClick={() => { setSelectedMethod('email'); setError(null) }}><EmailIcon className={style.icon} />Accedi con email</Button>
                    </div> : <form className={style.form} onSubmit={event => {
                        event.preventDefault();
                        if (resetPassword) handleResetPassword(); else signIn();
                    }}>
                        <label className={style.field}>Email
                            <input type="email" name="email" placeholder="nome@azienda.it" autoComplete="username" value={email} onChange={event => { setEmail(event.target.value); setError(null) }} disabled={loading || sending} required />
                        </label>
                        {!resetPassword && <>
                            <div className={style.field}><label htmlFor="admin-password">Password</label>
                                <span className={style.passwordField}>
                                    <input id="admin-password" type={showPassword ? 'text' : 'password'} name="password" placeholder="Inserisci la password" autoComplete="current-password" value={password} onChange={event => { setPassword(event.target.value); setError(null) }} disabled={loading} required />
                                    <button type="button" aria-label={showPassword ? 'Nascondi password' : 'Mostra password'} aria-pressed={showPassword} onClick={() => setShowPassword(value => !value)}>{showPassword ? 'Nascondi' : 'Mostra'}</button>
                                </span>
                            </div>
                            <Button type="button" appearance="text" additionalClass={style.forgot} onClick={handleChangeResetPassword} disabled={loading}>Password dimenticata?</Button>
                        </>}
                        {resetPassword && emailSent && <div className={style.success} role="status">
                            <strong>Richiesta inviata</strong>
                            <span>Controlla la tua casella email, anche nella cartella spam.</span>
                            <span>Puoi richiedere un nuovo link tra {timer} secondi.</span>
                        </div>}
                        <Button type="submit" status={loading || sending ? ButtonStatus.Loading : null} disabled={loading || sending || !email.trim() || !validateEmail(email.trim()) || (resetPassword ? emailSent : !password)}>
                            {resetPassword ? (emailSent ? 'Link inviato' : 'Invia link di recupero') : 'Accedi'}
                        </Button>
                        <Button type="button" appearance="subtle" disabled={loading || sending} onClick={() => {
                            if (resetPassword) handleChangeResetPassword(); else { setSelectedMethod(null); setError(null) }
                        }}><ArrowIcon className={style.backIcon} />{resetPassword ? "Torna all’accesso" : 'Altri metodi di accesso'}</Button>
                    </form>}
                    {error && <div className={style.error} role="alert">{error.message || 'Impossibile completare la richiesta. Riprova.'}</div>}
                    <p className={style.legal}>Accedendo accetti le <a href="/terms-and-conditions" target="_blank" rel="noreferrer">Condizioni d’uso</a> e la <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.</p>
                </section>
            </main>
        </FooterLayout>
    )
}

export default Login
